/*       */

import { loadData, mutateData, dispatchData, _getData } from 'app/utils/redux/action-utils';
import { graphql } from 'graphql/client';
import OptionsBuilder from 'app/utils/api/OptionsBuilder';
import { get } from 'app/utils/lo/lo';

import createRelationTaskToColumnMutation from 'graphql/abox/kanban/createRelationTaskToColumnMutation';
import createRelationColumnToBoardMutation from 'graphql/abox/kanban/createRelationColumnToBoardMutation';
import deleteRelationMutation from 'graphql/entities/relationships/deleteRelationMutation';
import loadAllBoardColumnsQuery from 'graphql/abox/boards/loadAllBoardColumnsQuery';
import loadColumnTasksQuery from 'graphql/abox/boards/loadColumnTasksQuery';
import loadBoardColumnsQuery from 'graphql/abox/boards/loadBoardColumnsQuery';
import loadBoardPrimaryClassQuery from 'graphql/abox/boards/loadBoardPrimaryClassQuery';
import updateRelationMutation from 'graphql/entities/relationships/updateRelationMutation';
import loadBoardRecordsQuery from 'graphql/abox/boards/loadBoardRecordsQuery';

export const BETA_BOARD_MAX_PER_COLUMN = 100;
const COLUMN_BATCH_SIZE = 5;

export const LOAD_COLUMNS_TASKS_STARTED = '@@affectli/abox/LOAD_COLUMNS_TASKS_STARTED';
export const LOAD_COLUMNS_TASKS = '@@affectli/abox/LOAD_COLUMNS_TASKS';
export const LOAD_ALL_COLUMNS_STARTED = '@@affectli/abox/LOAD_ALL_COLUMNS_STARTED';
export const LOAD_ALL_COLUMNS = '@@affectli/abox/LOAD_ALL_COLUMNS';

export const LOAD_BOARD_COLUMNS_STARTED = '@@affectli/abox/LOAD_BOARD_COLUMNS_STARTED';
export const LOAD_BOARD_COLUMNS = '@@affectli/abox/LOAD_BOARD_COLUMNS';

export const CREATE_RELATIONSHIP_ENTITY_TO_TASK_STARTED = '@@affectli/abox/CREATE_NEW_RELATIONSHIP_ENTITY_TO_TASK_STARTED';
export const CREATE_RELATIONSHIP_ENTITY_TO_TASK = '@@affectli/abox/CREATE_NEW_RELATIONSHIP_ENTITY_TO_TASK_DONE';

export const DELETE_RELATION_ENTITY_FROM_TASK_STARTED = '@@affectli/abox/REMOVE_TASK_RELATIONSHIP_FROM_COLUMN_STARTED';
export const DELETE_RELATION_ENTITY_FROM_TASK = '@@affectli/abox/REMOVE_TASK_RELATIONSHIP_FROM_COLUMN_DONE';

export const LOAD_BOARD_PRIMARY_CLASS_STARTED = '@@affectli/abox/LOAD_BOARD_PRIMARY_CLASS_STARTED';
export const LOAD_BOARD_PRIMARY_CLASS = '@@affectli/abox/LOAD_BOARD_PRIMARY_CLASS';

export const SHOW_NEW_COLUMN_TO_BOARD = '@@affectli/abox/ADD_NEW_BOARD_LABEL_TO_COLUMN';
export const HIDE_COLUMN_FROM_BOARD = '@@affectli/abox/HIDE_COLUMN_FROM_BOARD';

export const LOAD_BOARD_RECORDS_STARTED = '@@affectli/abox/LOAD_BOARD_RECORDS_STARTED';
export const LOAD_BOARD_RECORDS = '@@affectli/abox/LOAD_BOARD_RECORDS';

export const LOAD_BOARD_RECORDS_BY_COLUMN_STARTED = '@@affectli/abox/LOAD_BOARD_RECORDS_BY_COLUMN_STARTED';
export const LOAD_BOARD_RECORDS_BY_COLUMN = '@@affectli/abox/LOAD_BOARD_RECORDS_BY_COLUMN';

export const LOAD_BOARD_RECORDS_ALL_STARTED = '@@affectli/abox/LOAD_BOARD_RECORDS_ALL_STARTED';
export const LOAD_BOARD_RECORDS_ALL = '@@affectli/abox/LOAD_BOARD_RECORDS_ALL';

export const CREATE_RELATIONSHIP_ATTRIBUTE_TO_BOARD_STARTED = '@@affectli/abox/CREATE_RELATIONSHIP_COLUMN_TO_BOARD_STARTED';
export const CREATE_RELATIONSHIP_ATTRIBUTE_TO_BOARD_DONE = '@@affectli/abox/CREATE_RELATIONSHIP_COLUMN_TO_BOARD_DONE';

export const REMOVE_ATTRIBUTE_RELATIONSHIP_FROM_ENTITY_STARTED = '@@affectli/abox/LOAD_BOARD_PRIMARY_CLASS_STARTED';
export const REMOVE_ATTRIBUTE_RELATIONSHIP_FROM_ENTITY_DONE = '@@affectli/abox/LOAD_BOARD_PRIMARY_CLASS';

export const ADD_COLUMN_TO_BOARD = '@@affectli/abox/ADD_COLUMN_TO_BOARD';
export const SET_SELECTED_BOARD = '@@affectli/abox/SET_SELECTED_BOARD';
export const COLUMN_ORDER_POSITION = '@@affectli/abox/COLUMN_ORDER_POSITION';

export const UPDATE_COLUMN_TO_BOARD_RELATIONSHIP_STARTED = '@@affectli/abox/UPDATE_COLUMN_TO_BOARD_RELATIONSHIP_STARTED';
export const UPDATE_COLUMN_TO_BOARD_RELATIONSHIP_DONE = '@@affectli/abox/UPDATE_COLUMN_TO_BOARD_RELATIONSHIP_DONE';

/**
 * Load tasks per board-labels
 * Get tasks filter by relationship custom entity id
 * @param columnIds the entity ID
 * @param options startIndex, stopIndex, filterBy & orderBy query options
 */
const getFilterValue = options => {
    const relatedFilter = (options.filterBy || []).filter(({ field }) => field === 'relations.relatedEntity.id');
    const filterValue = get(relatedFilter, '[0].value', null);
    if (!filterValue) {
        return [];
    }
    if (Array.isArray(filterValue)) {
        return filterValue;
    }
    return [filterValue];
};

export const loadColumnsTasks = (columnId, options = {}) => {
    const { filterBy: columnFilterBy } = new OptionsBuilder()
        .filter({ field: 'relations.relatedEntity.id', op: 'in', value: columnId })
        .filter({ field: 'relations.relation.relationDefinition.relatedDescription', op: '=', value: 'Has Task' })
        .build();
    const variables = new OptionsBuilder(options).defaultOrder({ field: 'startDate', asc: false }).build();
    const modifyPayload = payload => {
        const filterValues = getFilterValue(options);
        if (filterValues?.length) {
            const records = payload.records.filter(record => record.relations.find(rel => filterValues.some(value => value === rel?.relatedEntity?.id)));
            return { columnId, ...payload, records };
        }
        return { columnId, ...payload };
    };
    return loadData(
        LOAD_COLUMNS_TASKS_STARTED,
        LOAD_COLUMNS_TASKS,
        loadColumnTasksQuery,
        modifyPayload
    )({ ...variables, filterBy: [...columnFilterBy, ...variables.filterBy] });
};

export const loadBoardPrimaryClass = () => {
    const variables = new OptionsBuilder({}).filter({ field: 'uri', op: '=', value: 'system_board' }).build();
    return loadData(LOAD_BOARD_PRIMARY_CLASS_STARTED, LOAD_BOARD_PRIMARY_CLASS, loadBoardPrimaryClassQuery, result => {
        return result[0];
    })(variables);
};

export const loadBoardRecordsByColumn = (columns, options = {}) =>
    dispatchData(
        LOAD_BOARD_RECORDS_BY_COLUMN_STARTED,
        LOAD_BOARD_RECORDS_BY_COLUMN,
        { columns, ...options },
        async () => {
            const {
                page = 1,
                pageSize = 10,
                filterBy = [],
                signal,
                ...rest
            } = options;
            if (typeof columns === 'string') {
                const columnId = columns;

                const startIndex = (page - 1) * pageSize;
                const stopIndex = startIndex + pageSize - 1;

                const response = await graphql.query({
                    query: loadBoardRecordsQuery,
                    variables: {
                        ...rest,
                        startIndex,
                        stopIndex,
                        filterBy
                    },
                    fetchPolicy: 'no-cache',
                    context: signal ? { fetchOptions: { signal } } : undefined
                });

                const payload = _getData(response);

                return {
                    mode: 'single',
                    columnId,
                    records: payload?.records || [],
                    count: payload?.count || 0,
                    page,
                    pageSize,
                    columnFilter: options.columnFilter // Preserve filter during pagination
                };
            }

            const filters = columns;
            const entries = [];

            // Batch loading: load COLUMN_BATCH_SIZE columns at a time
            for (let i = 0; i < filters.length; i += COLUMN_BATCH_SIZE) {
                const batch = filters.slice(i, i + COLUMN_BATCH_SIZE);

                const batchResults = await Promise.all(
                    batch.map(async filter => {
                        const startIndex = (page - 1) * pageSize;
                        const stopIndex = startIndex + pageSize - 1;
                        const response = await graphql.query({
                            query: loadBoardRecordsQuery,
                            variables: {
                                ...rest,
                                startIndex,
                                stopIndex,
                                filterBy: [...filterBy, filter],
                            },
                            fetchPolicy: 'no-cache',
                            context: signal ? { fetchOptions: { signal } } : undefined
                        });

                        const payload = _getData(response);

                        // Extract column ID from filter.value based on its type
                        const columnKey = Array.isArray(filter.value)
                            ? (filter.value[0]?.id || filter.value[0])  // Multiple: [{id: 'uuid'}] or ['string']
                            : filter.value;                              // Single: 'uuid' string

                        return [
                            columnKey,
                            {
                                records: payload?.records || [],
                                count: payload?.count || 0,
                                page: 1,
                                pageSize: 100,
                                columnFilter: filter, // Save filter for pagination
                            }
                        ];
                    })
                );
                
                entries.push(...batchResults);
            }

            return {
                mode: 'bulk',
                data: Object.fromEntries(entries),
            };
        }
    );



export const loadBoardRecordsAll = (options = {}) => {
    const { page = 1, pageSize = 10, ...rest } = options;

    const startIndex = (page - 1) * pageSize;
    const stopIndex = startIndex + pageSize - 1;
    return loadData(
        LOAD_BOARD_RECORDS_ALL_STARTED,
        LOAD_BOARD_RECORDS_ALL,
        loadBoardRecordsQuery,
    )(
        {
            startIndex,
            stopIndex,
            page,
            pageSize,
            ...rest,
        },
    );
};



export const loadBoardColumns = (options = {}) => {
    const variables = new OptionsBuilder(options).filter({ field: 'relation.relationDefinition.description', op: '=', value: 'parent of' }).build();
    return loadData(LOAD_BOARD_COLUMNS_STARTED, LOAD_BOARD_COLUMNS, loadBoardColumnsQuery)(variables);
};

export const loadAllBoardColumns = () => {
    const variables = new OptionsBuilder({})
        .filter({ field: 'classes.uri', op: '=', value: 'board_label' })
        .filter({ field: 'active', op: '=', value: true })
        .defaultOrder({ field: 'name', asc: true })
        .build();
    return loadData(LOAD_ALL_COLUMNS_STARTED, LOAD_ALL_COLUMNS, loadAllBoardColumnsQuery)(variables);
};

export const createRelationEntityToTask = record => {
    return mutateData(CREATE_RELATIONSHIP_ENTITY_TO_TASK_STARTED, CREATE_RELATIONSHIP_ENTITY_TO_TASK, createRelationTaskToColumnMutation)(record);
};

export const deleteRelationEntityFromTask = relation => {
    return mutateData(DELETE_RELATION_ENTITY_FROM_TASK_STARTED, DELETE_RELATION_ENTITY_FROM_TASK, deleteRelationMutation)({ relation });
};

export const showNewBoardColumn = column => dispatch => {
    dispatch({ type: SHOW_NEW_COLUMN_TO_BOARD, payload: column });
};

export const createRelationAttributeToEntity = record => {
    return mutateData(
        CREATE_RELATIONSHIP_ATTRIBUTE_TO_BOARD_STARTED,
        CREATE_RELATIONSHIP_ATTRIBUTE_TO_BOARD_DONE,
        createRelationColumnToBoardMutation,
        ''
    )(record);
};

export const deleteRelationAttributeFromEntity = relation => {
    return mutateData(
        REMOVE_ATTRIBUTE_RELATIONSHIP_FROM_ENTITY_STARTED,
        REMOVE_ATTRIBUTE_RELATIONSHIP_FROM_ENTITY_DONE,
        deleteRelationMutation,
        ''
    )({ relation });
};

/**
 * Update a relationship between two entities.
 * @param record
 * {
 *  "relation": (relation.id)
 *  "attributes": {
 *      "board-board-label/order": (index/orderNumber)
 *  }
 * }
 */
export const updateRelationColumnToBoard = record => {
    return mutateData(UPDATE_COLUMN_TO_BOARD_RELATIONSHIP_STARTED, UPDATE_COLUMN_TO_BOARD_RELATIONSHIP_DONE, updateRelationMutation, '')(record);
};

/**
 * Move column to another column position
 * @param columns reordered column position
 */
export const setColumnPosition = columns => dispatch => {
    dispatch({ type: COLUMN_ORDER_POSITION, payload: columns });
};

export const setSelectedBoard = board => dispatch => {
    dispatch({ type: SET_SELECTED_BOARD, payload: board });
};

export const cleanBoardColumns = data => dispatch => {
    dispatch({ type: LOAD_BOARD_COLUMNS, payload: data });
};
